import React, { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

export interface ModalProps {
  resolve: (value: unknown) => void
  reject: (error: Error) => void
  show?: boolean
  title?: string
  message?: string
  reasonRequired?: boolean
}

export const Modal: React.FC<ModalProps> = ({
  resolve,
  reject,
  show = true,
  title = 'Please confirm',
  message = 'Are you sure you want to perform this action?',
  reasonRequired = false,
}) => {
  const cancelButtonRef = useRef(null)
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (show) setReason('')
  }, [show])

  const trimmedReason = reason.trim()
  const canConfirm = !reasonRequired || trimmedReason !== ''

  return (
    <Transition
      show={show}
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <Dialog className="relative z-50" onClose={() => reject(new Error())}>
        <Transition
          show={show}
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 transition-opacity" />
        </Transition>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
              &#8203;
            </span>
            <Transition
              show={show}
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div
                role="dialog"
                className="relative transform overflow-hidden rounded-lg bg-white dark:bg-slate-900 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
              >
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <div role="heading" aria-level={3} className="text-lg leading-6 font-medium text-slate-900 dark:text-slate-100">
                      {title}
                    </div>
                    <div className="mt-2">
                      <p className="text-sm text-slate-500 dark:text-slate-400">{message}</p>
                    </div>
                    {reasonRequired ? (
                      <div className="mt-3 text-left">
                        <label
                          htmlFor="confirm-reason"
                          className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
                        >
                          Reason <span className="font-normal text-slate-500">(required)</span>
                        </label>
                        <textarea
                          id="confirm-reason"
                          className="block w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                          rows={3}
                          maxLength={500}
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 dark:bg-red-700 text-base font-medium text-white hover:bg-red-700 dark:hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-red-400 disabled:opacity-60 sm:ml-3 sm:w-auto sm:text-sm"
                    disabled={!canConfirm}
                    onClick={() => resolve(reasonRequired ? trimmedReason : null)}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-slate-300 dark:border-slate-600 shadow-sm px-4 py-2 bg-white dark:bg-slate-800 text-base font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 sm:mt-0 sm:w-auto sm:text-sm"
                    onClick={() => reject(new Error())}
                    ref={cancelButtonRef}
                  >
                    No
                  </button>
                </div>
              </div>
            </Transition>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default Modal;
